import { useCallback, useEffect, useRef, useState } from 'react';
import { sendRequest } from '../requests/useRequest';

export const copyErrorMessage = (response) => {
    const data = response && response.data;
    if (response && response.code === 401) return 'Tu sesión expiró. Inicia sesión nuevamente antes de continuar.';
    if (response && response.code === 403) return 'Tu usuario no tiene permiso para realizar esta operación.';
    if (data && typeof data.msg === 'string') return data.msg;
    if (data && typeof data.Msg === 'string') return data.Msg;
    if (data && data.errors) return Object.values(data.errors).flat().join(' ');
    if (response && response.code === 404) return 'No se encontró el modelo o el servicio de copia. Actualiza la lista y verifica que el backend esté disponible.';
    if (response && response.code === 409) return 'La configuración cambió o el destino está ocupado. Genera una nueva vista previa.';
    return 'No se pudo completar la solicitud. Verifica tu conexión e intenta nuevamente.';
};

export const useMachineModelConfigurationCopy = (onComplete) => {
    const [models, setModels] = useState([]);
    const [loadingModels, setLoadingModels] = useState(true);
    const [modelError, setModelError] = useState('');
    const [sourceId, setSourceId] = useState('');
    const [targetId, setTargetId] = useState('');
    const [codeEdits, setCodeEdits] = useState({});
    const [preview, setPreview] = useState(null);
    const [previewCurrent, setPreviewCurrent] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [phase, setPhase] = useState('idle');
    const [confirming, setConfirming] = useState(false);
    const mounted = useRef(false);
    const busy = useRef(false);
    const approvedRequest = useRef(null);
    const readController = useRef(null);

    const loadModels = useCallback(async () => {
        if (busy.current) return;
        readController.current && readController.current.abort();
        const controller = new AbortController();
        readController.current = controller;
        setLoadingModels(true);
        setModelError('');
        try {
            const response = await sendRequest({ method: 'GET', signal: controller.signal }, 'MachineModel/List');
            if (!mounted.current || controller.signal.aborted) return;
            if (response.code === 200 && Array.isArray(response.data)) {
                setModels(response.data);
            } else {
                setModelError(copyErrorMessage(response));
            }
        } catch (_) {
            if (mounted.current && !controller.signal.aborted) setModelError(copyErrorMessage());
        } finally {
            if (mounted.current && !controller.signal.aborted) setLoadingModels(false);
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        loadModels();
        return () => {
            mounted.current = false;
            // Only reads are cancelled. An insertion may already have committed on the server.
            readController.current && readController.current.abort();
        };
    }, [loadModels]);

    const invalidate = () => {
        approvedRequest.current = null;
        setPreviewCurrent(false);
        setConfirming(false);
        setError('');
    };

    const selectModel = (type, id) => {
        if (busy.current || result) return;
        invalidate();
        setPreview(null);
        setCodeEdits({});
        if (type === 'source') setSourceId(id);
        else setTargetId(id);
    };

    const editCode = (id, value) => {
        if (busy.current || result) return;
        invalidate();
        setCodeEdits(previous => ({ ...previous, [id]: value }));
    };

    const resetCodes = () => {
        if (busy.current || result) return;
        invalidate();
        setCodeEdits({});
        setPreview(previous => previous && ({ ...previous,
            app_codes: previous.app_codes.map(code => ({ ...code, target_code: code.source_code })) }));
    };

    const validModels = Number(sourceId) > 0 && Number(targetId) > 0 && Number(sourceId) !== Number(targetId);
    const emptyCodes = Object.values(codeEdits).some(value => !value.trim());
    const canPreview = validModels && !emptyCodes && !loadingModels && !modelError && phase === 'idle' && !result;
    const canCopy = canPreview && previewCurrent && preview && preview.can_execute === true
        && preview.conflicts.length === 0 && !!preview.preview_hash;

    const generatePreview = async () => {
        if (busy.current || !canPreview) return;
        busy.current = true;
        invalidate();
        setPhase('preview');
        const request = {
            source_machine_model_id: Number(sourceId),
            target_machine_model_id: Number(targetId),
            app_code_overrides: Object.keys(codeEdits).sort((a, b) => Number(a) - Number(b))
                .filter(id => !preview || !preview.app_codes.some(code => String(code.source_app_code_id) === id && code.source_code === codeEdits[id].trim()))
                .map(id => ({ source_app_code_id: Number(id), target_code: codeEdits[id].trim() }))
        };
        const controller = new AbortController();
        readController.current = controller;
        try {
            const response = await sendRequest({ method: 'POST', body: JSON.stringify(request), signal: controller.signal }, 'MachineModel/PreviewConfigurationCopy');
            if (!mounted.current || controller.signal.aborted) return;
            if (response.code === 200 && response.data && response.data.preview_hash) {
                setPreview(response.data);
                // Retain overrides across subsequent previews, even if only one code is edited next.
                setCodeEdits(Object.fromEntries(response.data.app_codes
                    .filter(code => code.target_code !== code.source_code)
                    .map(code => [code.source_app_code_id, code.target_code])));
                approvedRequest.current = request;
                setPreviewCurrent(true);
            } else {
                setError(copyErrorMessage(response));
            }
        } catch (_) {
            if (mounted.current) setError(copyErrorMessage());
        } finally {
            busy.current = false;
            if (mounted.current) setPhase('idle');
        }
    };

    const executeCopy = async () => {
        if (busy.current || !canCopy || !approvedRequest.current || !confirming) return;
        busy.current = true;
        const request = { ...approvedRequest.current, preview_hash: preview.preview_hash };
        // A hash can only be submitted once from this view, even if the response is lost.
        approvedRequest.current = null;
        setPreviewCurrent(false);
        setConfirming(false);
        setError('');
        setPhase('copy');
        try {
            const response = await sendRequest({ method: 'POST', body: JSON.stringify(request) }, 'MachineModel/CopyConfiguration');
            if (!mounted.current) return;
            if (response.code === 200 && response.data && response.data.id_mappings) {
                setResult(response.data);
                if (onComplete) onComplete();
            } else if (response.code === 409) {
                if (response.data && Array.isArray(response.data.conflicts)) setPreview(response.data);
                setError(`${copyErrorMessage(response)} Revisa los detalles y solicita una nueva vista previa antes de continuar.`);
            } else if (!response.code || response.code >= 500 || response.code === 200) {
                setError('No se pudo confirmar el resultado de la copia. Revisa la configuración del destino y genera una nueva vista previa antes de reintentar. La solicitud no se repetirá automáticamente.');
            } else {
                setError(copyErrorMessage(response));
            }
        } catch (_) {
            if (mounted.current) setError('No se pudo confirmar el resultado de la copia. Revisa el destino y genera una nueva vista previa antes de reintentar.');
        } finally {
            busy.current = false;
            if (mounted.current) setPhase('idle');
        }
    };

    const reset = () => {
        if (busy.current) return;
        invalidate();
        setSourceId('');
        setTargetId('');
        setCodeEdits({});
        setPreview(null);
        setResult(null);
    };

    return {
        models, loadingModels, modelError, loadModels, sourceId, targetId, selectModel,
        codeEdits, editCode, resetCodes, emptyCodes, preview, previewCurrent, result, error, phase,
        canPreview, canCopy, generatePreview, confirming, setConfirming, executeCopy, reset
    };
};
